import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';
import { BrandSectionLayout } from './BrandSectionLayout';

interface BrandLogoSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const BrandLogoSection: React.FC<BrandLogoSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeImageType, setActiveImageType] = useState<'primary' | 'original' | 'light' | 'dark'>('primary');

  // Get colors from brand data
  const primaryColor = state.brandData?.colors?.primary || '#3B82F6';

  const handleImageSelect = (imageUrl: string, imageData?: any) => {
    console.log('Image selected:', { imageUrl, imageData, activeImageType });

    // Ensure we have a valid URL
    if (!imageUrl) {
      console.error('No image URL provided');
      return;
    }

    try {
      if (activeImageType === 'primary') {
        console.log('Updating primary logo with:', imageUrl);
        updateSectionContent('primaryLogo', imageUrl);
      } else if (activeImageType === 'original') {
        console.log('Updating original logo with:', imageUrl);
        updateSectionContent('originalLogo', imageUrl);
      } else if (activeImageType === 'light') {
        console.log('Updating light logo with:', imageUrl);
        updateSectionContent('lightLogo', imageUrl);
      } else {
        console.log('Updating dark logo with:', imageUrl);
        updateSectionContent('darkLogo', imageUrl);
      }
      console.log('Image update completed');
    } catch (error) {
      console.error('Error updating image:', error);
    }

    setShowImageModal(false);
  };

  const openImageModal = (imageType: 'primary' | 'original' | 'light' | 'dark') => {
    setActiveImageType(imageType);
    setShowImageModal(true);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Two-column layout: 30% left, 70% right */}
      <div className="h-full flex gap-16">
        {/* Left Column - 30% width with description */}
        <div className="flex flex-col" style={{ width: '30%' }}>
          {/* Brand Logo Title and Description */}
          <div className="flex flex-col items-start gap-4">
            <EditableText
              value={section.content.logoTitle || 'Brand Logo'}
              onChange={(value) => updateSectionContent('logoTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold mb-8"
              placeholder="Brand Logo"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'logoTitle'}
              onEditStart={() => setEditingElement('logoTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('logoTitle', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                fontWeight: 'bold',
                ...section.content.logoTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />

            <EditableText
              value={section.content.logoDescription || 'brand\'s logo represents our brand identity and values'}
              onChange={(value) => updateSectionContent('logoDescription', value)}
              element="p"
              className="text-lg leading-relaxed"
              placeholder="Brand logo represents our brand identity and values"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'logoDescription'}
              onEditStart={() => setEditingElement('logoDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('logoDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.logoDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>
        </div>

        {/* Right Column - 70% width with 2x2 logo grid */}
        <div style={{ width: '70%' }}>
          <div className="grid grid-cols-2 grid-rows-2 gap-4 h-full">

            {/* Top Left - Primary Color Background */}
            <div
              className="relative group rounded-md overflow-hidden flex items-center justify-center p-16"
              style={{
                backgroundColor: primaryColor,
                borderRadius: '0.375rem'
              }}
            >
              <img
                src={section.content.primaryLogo || '/img-placeholder-16-9.png'}
                alt="Logo on Primary Background"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Upload className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-xs font-medium mb-2">Primary</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageModal('primary');
                      }}
                      className="px-2 py-1 bg-white/90 text-black rounded text-xs hover:bg-white transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Top Right - Original (White Background) */}
            <div
              className="relative group rounded-md overflow-hidden flex items-center justify-center p-16"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
            >
              <img
                src={section.content.originalLogo || '/img-placeholder-16-9.png'}
                alt="Original Logo"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Upload className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-xs font-medium mb-2">Original</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageModal('original');
                      }}
                      className="px-2 py-1 bg-white/90 text-black rounded text-xs hover:bg-white transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Left - White Background */}
            <div
              className="relative group rounded-md overflow-hidden flex items-center justify-center p-16"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
            >
              <img
                src={section.content.lightLogo || '/img-placeholder-16-9.png'}
                alt="Logo on Light Background"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Upload className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-xs font-medium mb-2">White BG</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageModal('light');
                      }}
                      className="px-2 py-1 bg-white/90 text-black rounded text-xs hover:bg-white transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Right - Dark Background */}
            <div
              className="relative group rounded-md overflow-hidden flex items-center justify-center p-16"
              style={{
                backgroundColor: '#1F2937',
                borderRadius: '0.375rem'
              }}
            >
              <img
                src={section.content.darkLogo || '/img-placeholder-16-9.png'}
                alt="Logo on Dark Background"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Upload className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-xs font-medium mb-2">Dark BG</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageModal('dark');
                      }}
                      className="px-2 py-1 bg-white/90 text-black rounded text-xs hover:bg-white transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
      />
    </BrandSectionLayout>
  );
};